import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExploreIcon from '@mui/icons-material/Explore';
import VerifiedIcon from '@mui/icons-material/Verified';
import PaymentIcon from '@mui/icons-material/Payment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink } from 'react-router-dom';

const travelerSteps = [
  {
    step: 1,
    icon: <SearchIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
    title: 'Search & Discover',
    description:
      'Browse through hundreds of verified local guides across multiple destinations. Filter by language, specialty, interests, and budget to find your perfect match.',
    details: [
      'Filter by destination and expertise',
      'Read verified reviews and ratings',
      'View guide profiles and specialties',
      'Check availability in real-time',
    ],
    image: '/images/destinations/search-map.jpg',
    imageAlt: 'Traveler planning a destination on a map',
  },
  {
    step: 2,
    icon: <PersonSearchIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
    title: 'Choose Your Guide',
    description:
      'Compare guide profiles, read authentic reviews from past travelers, and select the guide who best matches your interests and travel style.',
    details: [
      'Compare multiple guide profiles',
      'View photos and videos of past tours',
      'Check language proficiency',
      'See pricing and availability',
    ],
    image: '/images/destinations/choose-guide.jpg',
    imageAlt: 'Local guide ready to share their city',
  },
  {
    step: 3,
    icon: <EventAvailableIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
    title: 'Book Securely',
    description:
      'Send a booking request with your preferred dates and details. Once confirmed by your guide, make a secure payment through our platform.',
    details: [
      'Request your preferred dates',
      'Customize your experience',
      'Secure payment processing',
      'Instant booking confirmation',
    ],
    image: '/images/destinations/book-secure.jpg',
    imageAlt: 'Secure online booking and payment',
  },
  {
    step: 4,
    icon: <ExploreIcon sx={{ fontSize: { xs: 36, md: 48 } }} />,
    title: 'Explore & Enjoy',
    description:
      'Meet your guide and embark on an unforgettable journey. Experience the destination through local eyes and create memories that last a lifetime.',
    details: [
      'Meet your local guide',
      'Discover hidden gems',
      'Authentic local experiences',
      'Share your review after',
    ],
    image: '/images/destinations/explore-local.jpg',
    imageAlt: 'Travelers exploring a destination with a local guide',
  },
];

const guideSteps = [
  {
    step: 1,
    title: 'Create Your Profile',
    description:
      'Sign up as a guide and build your professional profile showcasing your expertise, languages, and specialties.',
  },
  {
    step: 2,
    title: 'Get Verified',
    description:
      'Complete our verification process to ensure traveler safety and build trust in the community.',
  },
  {
    step: 3,
    title: 'Receive Bookings',
    description:
      'Start receiving booking requests from travelers interested in your unique experiences.',
  },
  {
    step: 4,
    title: 'Share & Earn',
    description: 'Lead amazing tours, share your passion, and earn income doing what you love.',
  },
];

const features = [
  {
    icon: <VerifiedIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />,
    title: 'Verified Guides',
    description: 'All guides undergo thorough verification and background checks for your safety.',
  },
  {
    icon: <PaymentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />,
    title: 'Secure Payments',
    description:
      'Your payments are protected with industry-standard encryption and secure processing.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />,
    title: '24/7 Support',
    description: 'Our support team is always available to help you with any questions or concerns.',
  },
  {
    icon: <StarIcon sx={{ fontSize: { xs: 32, md: 40 }, color: 'primary.main' }} />,
    title: 'Quality Guaranteed',
    description:
      'Read authentic reviews and ratings to ensure you get the best experience possible.',
  },
];

export function HowItWorksPage() {
  return (
    <Box sx={{ width: '100%', overflowX: 'clip' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 260, sm: 320, md: 420 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url("/images/destinations/explore-local.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: '#FFFFFF',
          textAlign: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(15, 23, 42, 0.55)',
          },
        }}
      >
        <Container
          maxWidth="md"
          sx={{ position: 'relative', zIndex: 1, py: { xs: 5, md: 6 }, px: { xs: 2.5, sm: 3 } }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: { xs: 1.5, md: 3 },
              color: '#FFFFFF',
              textShadow: '0 2px 12px rgba(0,0,0,0.35)',
              lineHeight: 1.2,
            }}
          >
            How Triplin Works
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', sm: '1.125rem', md: '1.5rem' },
              fontWeight: 400,
              lineHeight: 1.6,
              maxWidth: 700,
              mx: 'auto',
              color: 'rgba(255,255,255,0.92)',
              textShadow: '0 1px 6px rgba(0,0,0,0.3)',
            }}
          >
            Connect with authentic local guides in four simple steps
          </Typography>
        </Container>
      </Box>

      {/* For Travelers Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 1.5,
              display: 'block',
            }}
          >
            For Travelers
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              mb: 1.5,
              fontSize: { xs: '1.5rem', md: '2.75rem' },
              lineHeight: 1.2,
            }}
          >
            Your Journey Starts Here
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.9375rem', md: '1rem' },
            }}
          >
            Follow these simple steps to book your perfect local guide
          </Typography>
        </Box>

        <Stack spacing={{ xs: 3, md: 5 }}>
          {travelerSteps.map((step, index) => (
            <Paper
              key={step.step}
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: { xs: 2, md: 3 },
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: { md: '0 12px 24px rgba(0,0,0,0.08)' },
                },
              }}
            >
              <Grid
                container
                spacing={{ xs: 2.5, md: 4 }}
                alignItems="center"
                direction={{
                  xs: 'column',
                  md: index % 2 === 1 ? 'row-reverse' : 'row',
                }}
              >
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box
                    component="img"
                    src={step.image}
                    alt={step.imageAlt}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: { xs: 180, sm: 220, md: 260 },
                      objectFit: 'cover',
                      borderRadius: { xs: 1.5, md: 2 },
                      display: 'block',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: 2,
                      mb: 2,
                      flexDirection: { xs: 'row', sm: 'row' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 56, md: 72 },
                        height: { xs: 56, md: 72 },
                        borderRadius: '50%',
                        bgcolor: alpha('#1976d2', 0.1),
                        color: 'primary.main',
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      {step.icon}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          width: { xs: 26, md: 32 },
                          height: { xs: 26, md: 32 },
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: { xs: '0.8125rem', md: '1rem' },
                        }}
                      >
                        {step.step}
                      </Box>
                    </Box>
                    <Typography
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', md: '1.75rem' },
                        lineHeight: 1.3,
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      color: 'text.secondary',
                      mb: 2.5,
                      lineHeight: 1.75,
                      fontSize: { xs: '0.9375rem', md: '1rem' },
                    }}
                  >
                    {step.description}
                  </Typography>

                  <Grid container spacing={{ xs: 1.25, sm: 2 }}>
                    {step.details.map((detail) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={detail}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                              mt: 1,
                            }}
                          />
                          <Typography
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.875rem', md: '0.9375rem' },
                              lineHeight: 1.5,
                            }}
                          >
                            {detail}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>

        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
          <Button
            component={RouterLink}
            to="/guides"
            variant="contained"
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Find Your Guide
          </Button>
        </Box>
      </Container>

      {/* For Guides Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 5, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1.5,
                display: 'block',
              }}
            >
              For Guides
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: 1.5,
                fontSize: { xs: '1.5rem', md: '2.75rem' },
                lineHeight: 1.2,
              }}
            >
              Share Your Passion, Earn Income
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '0.9375rem', md: '1rem' },
              }}
            >
              Join our community of local guides and start earning by sharing your city
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, md: 4 }}>
            {guideSteps.map((step) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={step.step}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    bgcolor: 'white',
                    borderRadius: { xs: 2, md: 3 },
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      transform: { md: 'translateY(-8px)' },
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      mx: 'auto',
                      mb: 2.5,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Typography
                    sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '1.0625rem', md: '1.25rem' } }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
            <Button
              component={RouterLink}
              to="/become-guide"
              variant="contained"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Become a Guide
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Trust & Safety Features */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: 1.5,
              mb: 1.5,
              display: 'block',
            }}
          >
            Trust & Safety
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontWeight: 700,
              mb: 1.5,
              fontSize: { xs: '1.5rem', md: '2.75rem' },
              lineHeight: 1.2,
            }}
          >
            Your Safety is Our Priority
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.9375rem', md: '1rem' },
            }}
          >
            We&apos;ve built Triplin with safety and trust at its core
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {features.map((feature) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={feature.title}>
              <Box sx={{ textAlign: 'center', px: { xs: 1, md: 0 } }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    borderRadius: '50%',
                    bgcolor: alpha('#1976d2', 0.1),
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.0625rem', md: '1.25rem' } }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.7,
                    fontSize: { xs: '0.875rem', md: '0.9375rem' },
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 5, md: 12 },
          backgroundImage: 'url("/images/destinations/guide-travelers.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          color: '#FFFFFF',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(15, 23, 42, 0.72)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 2.5, sm: 3 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontWeight: 700,
                mb: { xs: 2, md: 3 },
                fontSize: { xs: '1.5rem', md: '2.75rem' },
                color: '#FFFFFF',
                lineHeight: 1.2,
              }}
            >
              Ready to Start Your Adventure?
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                lineHeight: 1.75,
                mb: { xs: 3, md: 4 },
                opacity: 0.95,
                fontSize: { xs: '0.9375rem', md: '1.125rem' },
                color: '#FFFFFF',
              }}
            >
              Join thousands of travelers who have discovered the magic of exploring destinations
              with local guides. Your authentic travel experience is just a few clicks away.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              <Button
                component={RouterLink}
                to="/guides"
                variant="contained"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: 'white',
                  color: 'primary.main',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                Browse Guides
              </Button>
              <Button
                component={RouterLink}
                to="/about"
                variant="outlined"
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: 'white',
                  color: 'white',
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Learn More
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
